import type { ReactNode } from 'react';
import cx from 'clsx';
import { Icon } from 'components/Icon';
import { clearIcon } from 'styles/style.css';
import * as styles from './ChainTargetEntry.css';
import type { ChainNode, ChainPath, ChainTarget } from './types';
import { resolveTargetPath } from './navigation';

export type ChainSelectionStatus = 'unresolved' | 'loading' | 'valid' | 'invalid' | 'error';

export type ChainTargetEntryProps = {
  value: ChainTarget | null;
  /**
   * Resolved ancestry for the saved value, supplied independently of discovery
   * results. Without it the target falls back to its kind and id.
   */
  path?: ChainPath;
  label?: ReactNode;
  selectionStatus?: ChainSelectionStatus;
  /** Rendered only when the target is clearable by the caller. */
  onClear?: () => void;
  clearDisabled?: boolean;
  /** Replaces the default chain segments, e.g. a rich ProductTitle. */
  renderTarget?: ReactNode;
  emptyMessage?: ReactNode;
  className?: string;
};

function kindLabel(kind: ChainNode['kind']): string {
  return kind === 'service-category' ? 'Service category' : `${kind[0].toUpperCase()}${kind.slice(1)}`;
}

/**
 * Presentation of the selected chain target: ancestry, target display, clear
 * affordance, and adapter-supplied resolution status. Owns no navigation and
 * emits no selection; callers place it wherever the committed draft is shown.
 */
export function ChainTargetEntry({
  value = null,
  path,
  label,
  selectionStatus = 'unresolved',
  onClear,
  clearDisabled = false,
  renderTarget,
  emptyMessage = 'No target selected',
  className,
}: ChainTargetEntryProps): ReactNode {
  const ancestors = value ? resolveTargetPath(value, path) : [];
  const segments = ancestors.length ? ancestors : value ? [{ ...value, label: String(value.id) }] : [];

  return (
    <div className={cx(styles.container, className)}>
      {label ? <div className={styles.label}>{label}</div> : null}
      <div className={styles.summary} aria-live="polite">
        {value ? (
          <>
            <div className={styles.selection}>
              {renderTarget ? (
                <div className={styles.target}>{renderTarget}</div>
              ) : (
                <ol className={styles.path} aria-label="Selected path">
                  {segments.map((node, index) => (
                    <li className={styles.segment} key={`${node.kind}-${node.id}`}>
                      {index > 0 && (
                        <span className={styles.separator} aria-hidden="true">
                          ›
                        </span>
                      )}
                      <span className={styles.node} data-endpoint={index === segments.length - 1 || undefined}>
                        <span className={styles.kind}>{kindLabel(node.kind)}</span>
                        <span className={styles.nodeLabel}>{node.label || node.id}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              )}
              {onClear && (
                <button
                  type="button"
                  className={styles.clear}
                  onClick={onClear}
                  disabled={clearDisabled}
                  aria-label="Clear product chain target"
                >
                  <Icon icon="circle-xmark" className={clearIcon} />
                </button>
              )}
            </div>
            {selectionStatus === 'invalid' && (
              <div className={styles.invalid}>This target is outside the current scope.</div>
            )}
            {selectionStatus === 'loading' && <div role="status">Checking selected target…</div>}
            {selectionStatus === 'error' && <div role="alert">Could not check the selected target.</div>}
          </>
        ) : (
          <div className={styles.empty}>{emptyMessage}</div>
        )}
      </div>
    </div>
  );
}
